import { useDisclosure } from "@mantine/hooks";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/shared/Button/Button";
import AuthorizationModal from "src/generator/_Modals/AuthorizationModal/AuthorizationModal";
import { mapUsers } from "src/generator/utils/mapUsers";
import { useGetApi, usePostApi } from "src/hooks";
import { type ICredentials } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import { getYachtId } from "src/utils";

import { setToVerify } from "../../../redux/slices/verifySlice";

export const usersMapper = (data: any) => {
  if (!data?.list || !Array.isArray(data.list)) {
    return [];
  }

  return mapUsers(data.list, "", "role firstname surname");
};

interface Props {
  request: string;
  navigatePath: string;
  title?: string;
  refetch?: () => void;
}

const VerifyButton = ({ request, navigatePath, title, refetch }: Props) => {
  const dispatch = useDispatch();
  const { post, isLoading } = usePostApi();
  const [opened, { open, close }] = useDisclosure(false);
  const toVerify = useSelector(state => state.verifySlice.toVerify);
  const navigate = useNavigate();

  const { data: users, refetch: fetchUsers } = useGetApi({
    request: "get-users-to-verify",
    body: { params: { yachtid: getYachtId() } },
    mapper: usersMapper,
    fetchOnInit: false,
  });

  const handleOpen = useCallback(async () => {
    await fetchUsers();
    open();
  }, [open, fetchUsers]);

  const handleVerify = useCallback(
    async ({ userid: user, ...credentials }: ICredentials) => {
      const list = toVerify.map(e => ({
        [e.idType]: e.id,
      }));
      await post({
        request,
        body: { params: { ...credentials, user, list } },
        onSuccess: r => {
          navigate(navigatePath);
          dispatch(setToVerify([]));
          close();
          refetch && refetch();
        },
      });
    },
    [toVerify, post, request, navigate, navigatePath, dispatch, close, refetch],
  );

  return (
    <>
      <Button
        disabled={toVerify.length === 0}
        onClick={
          handleOpen
        }>{`${title ?? "Verify"} ${toVerify.length || ""}`}</Button>
      <AuthorizationModal
        opened={opened}
        close={close}
        onSubmit={handleVerify}
        loading={isLoading}
        users={users}
      />
    </>
  );
};

export default VerifyButton;
